import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { TeamOutlined, SafetyOutlined } from '@ant-design/icons';
import SideNavbar from '../../components/Navbar/SideNavbar';
import Header from '../../components/Header/Header';
import UsersManagement from './UsersManagement';
import RolesManagement from './RolesManagement';
import { RoleDTO } from './types';
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';

const UserManagement: React.FC = () => {
    const [roles, setRoles] = useState<RoleDTO[]>([]);
    const [loading, setLoading] = useState(false);
    // @@ AI-COMMENT: Local tab state replaces the antd Tabs component so the page matches the CMS tab pattern
    const [activeTab, setActiveTab] = useState<'users' | 'roles'>('users');

    // @@ AI-COMMENT: Fetch all roles for dropdown use across components
    const fetchRoles = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(apis.USER_MANAGEMENT_ROLE_LIST);
            if (response.data?.status && response.data.data) {
                setRoles(response.data.data);
            } else {
                message.error('Failed to fetch roles');
            }
        } catch (error: any) {
            console.error('Error fetching roles:', error);
            message.error(error?.response?.data?.statusDescription || 'Failed to fetch roles');
        } finally {
            setLoading(false);
        }
    };

    // @@ AI-COMMENT: Initial load of roles
    useEffect(() => {
        fetchRoles();
    }, []);

    // @@ AI-COMMENT: Callback to refresh roles when a new role is created/updated/deleted
    const handleRoleChange = () => {
        fetchRoles();
    };

    return (
        <div className="flex mainWrapper w-full">
            <div className="sidenav-wrapper">
                <SideNavbar />
            </div>
            <div className="right-container">
                <div className="header-container">
                    <Header />
                </div>
                <div className="body-container">
                    {/* @@ AI-COMMENT: Page header matching the shared CMS listing header */}
                    <div className="p-4">
                        <p className="text-xl font-bold text-[#43425D]">USER MANAGEMENT</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">Manage users, roles, and access permissions</p>
                    </div>

                    {/* @@ AI-COMMENT: Tab navigation for Users and Roles, using the CMS underline tab pattern */}
                    <div className="bg-white border-b border-gray-200 px-4">
                        <nav className="flex space-x-6">
                            <button
                                onClick={() => setActiveTab('users')}
                                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-xs font-semibold ${activeTab === 'users'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <TeamOutlined />
                                Users
                            </button>
                            <button
                                onClick={() => setActiveTab('roles')}
                                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-xs font-semibold ${activeTab === 'roles'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <SafetyOutlined />
                                Roles
                            </button>
                        </nav>
                    </div>

                    {activeTab === 'users' ? (
                        <UsersManagement roles={roles} />
                    ) : (
                        <RolesManagement
                            roles={roles}
                            onRoleChange={handleRoleChange}
                            loading={loading}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserManagement;